<template>
  <FormPage
    title="Новый исполнитель"
    description="Соберите исполнителя из компании, менеджера, водителя, машины и прицепа. Можно выбрать существующие записи или создать новые."
  >
    <template #controls>
      <label class="split-toggle"><input v-model="split" type="checkbox" /> Раздельный режим</label>
      <button class="btn-outline back-list-button" type="button" @click="goToList">Назад к списку</button>
    </template>

    <div class="hub-progress">
      <span class="progress-label">Готовность:</span>
      <div class="progress-track">
        <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
      </div>
      <span class="progress-count">{{ progressDone }} / 4</span>
      <div v-if="wizard.carTrailerError || createError" class="progress-error">
        {{ wizard.carTrailerError || createError }}
      </div>
      <button
        v-if="!isSaving && wizard.isValid && !wizard.carTrailerError"
        class="select-button primary"
        type="button"
        @click="createExecutor"
      >Добавить исполнителя</button>
      <div v-if="isSaving" class="saving">Сохранение…</div>
      <button
        v-if="hasAnySelection"
        class="btn-outline btn-clear"
        type="button"
        @click="confirmClear"
      >Сбросить</button>
    </div>

    <ExecutorSplitWorkspace
      v-if="split"
      mode="wizard"
      :items="wizard.selectItems"
      :summary-title="wizard.isValid ? 'Готово к сохранению' : `Не хватает полей: ${4 - progressDone}`"
      :save-handler="saveSplitForm"
      @select="onItemSelect"
      @replace="onItemSelect($event, null)"
    >
    </ExecutorSplitWorkspace>
    <FormGrid v-else :cols="3">
        <ExecutorHubCard title="Компания" :filled="!!wizard.selectItems.companies" clickable @click="openCard('companies', 'company')">
        <template #actions>
          <ExecutorHubActions
            :editable="!!wizard.selectItems.companies"
            :replaceable="!!wizard.selectItems.companies"
            :createable="!wizard.selectItems.companies"
            @edit="editItem('companies')"
            @replace="onCompanySelect(null)"
            @create="goStep('company')"
          />
        </template>
        <EntitySmartSearch
          model="companies"
          :itemSelect="wizard.selectItems.companies"
          :editable="true"
          :show-actions="false"
          @update:itemSelect="onCompanySelect"
          @edit="editItem('companies')"
        />
        </ExecutorHubCard>

        <ExecutorHubCard title="Менеджер" :filled="!!wizard.selectItems.managers" clickable @click="openCard('managers', 'manager')">
        <template #actions>
          <ExecutorHubActions
            :editable="!!wizard.selectItems.managers"
            :replaceable="!!wizard.selectItems.managers"
            :createable="!wizard.selectItems.managers"
            @edit="editItem('managers')"
            @replace="onItemSelect('managers', null)"
            @create="goStep('manager')"
          />
        </template>
        <EntitySmartSearch
          model="managers"
          :itemSelect="wizard.selectItems.managers"
          :companyId="wizard.selectItems.companies && wizard.selectItems.companies.id"
          :editable="true"
          :show-actions="false"
          @update:itemSelect="onItemSelect('managers', $event)"
          @edit="editItem('managers')"
        />
        </ExecutorHubCard>

        <ExecutorHubCard title="Водитель" :filled="!!wizard.selectItems.drivers" clickable @click="openCard('drivers', 'driver')">
        <template #actions>
          <ExecutorHubActions
            :editable="!!wizard.selectItems.drivers"
            :replaceable="!!wizard.selectItems.drivers"
            :createable="!wizard.selectItems.drivers"
            @edit="editItem('drivers')"
            @replace="onItemSelect('drivers', null)"
            @create="goStep('driver')"
          />
        </template>
        <EntitySmartSearch
          model="drivers"
          :itemSelect="wizard.selectItems.drivers"
          :editable="true"
          :show-actions="false"
          @update:itemSelect="onItemSelect('drivers', $event)"
          @edit="editItem('drivers')"
        />
        </ExecutorHubCard>

        <ExecutorHubCard title="Автомобиль" :filled="!!wizard.selectItems.cars" clickable @click="openCard('cars', 'car')">
        <template #actions>
          <ExecutorHubActions
            :editable="!!wizard.selectItems.cars"
            :replaceable="!!wizard.selectItems.cars"
            :createable="!wizard.selectItems.cars"
            @edit="editItem('cars')"
            @replace="onItemSelect('cars', null)"
            @create="goStep('car')"
          />
        </template>
        <EntitySmartSearch
          model="cars"
          :itemSelect="wizard.selectItems.cars"
          :editable="true"
          :show-actions="false"
          @update:itemSelect="onItemSelect('cars', $event)"
          @edit="editItem('cars')"
        />
        </ExecutorHubCard>

        <ExecutorHubCard title="Прицеп" :filled="!!wizard.selectItems.trailers" clickable @click="openCard('trailers', 'trailer')">
        <template #actions>
          <ExecutorHubActions
            :editable="!!wizard.selectItems.trailers"
            :replaceable="!!wizard.selectItems.trailers"
            :createable="!wizard.selectItems.trailers"
            @edit="editItem('trailers')"
            @replace="onItemSelect('trailers', null)"
            @create="goStep('trailer')"
          />
        </template>
        <EntitySmartSearch
          model="trailers"
          :itemSelect="wizard.selectItems.trailers"
          :editable="true"
          :show-actions="false"
          @update:itemSelect="onItemSelect('trailers', $event)"
          @edit="editItem('trailers')"
        />
        </ExecutorHubCard>

    </FormGrid>
  </FormPage>
</template>

<script>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import EntitySmartSearch from '@/components/UI/EntitySmartSearch.vue'
import ExecutorHubCard from '@/components/UI/ExecutorHubCard.vue'
import ExecutorHubActions from '@/components/UI/ExecutorHubActions.vue'
import ExecutorSplitWorkspace from '@/components/UI/ExecutorSplitWorkspace.vue'
import { useExecutorWizardStore } from '@/stores/executorWizard'
import { useWizardBackGuard } from '@/composables/useWizardBackGuard'
import { useExecutorSplitMode } from '@/composables/useExecutorSplitMode'
import { fetchCarById } from '@/backend/carsApi'

export default {
  name: 'ExecutorWizardHub',
    components: { EntitySmartSearch, ExecutorHubCard, ExecutorHubActions, ExecutorSplitWorkspace },
  setup() {
    const store = useStore()
    const router = useRouter()
    const wizard = useExecutorWizardStore()
    const { split } = useExecutorSplitMode()

    useWizardBackGuard({ name: 'Executors' })

    const isSaving = ref(false)
    const createError = ref(null)
    const progressDone = computed(() => {
      const s = wizard.selectItems
      return [s.companies, s.managers, s.drivers, s.cars].filter(Boolean).length
    })
    const progressPercent = computed(() => (progressDone.value / 4) * 100)

    const hasAnySelection = computed(() =>
      Object.values(wizard.selectItems).some(Boolean),
    )

    function onCompanySelect(value) {
      wizard.setCompany(value)
    }

    const vehicleSelectionRequests = { cars: 0, trailers: 0 }

    async function onItemSelect(model, value) {
      wizard.setItem(model, value)
      if (!value?.id || !Object.hasOwn(vehicleSelectionRequests, model)) return

      const requestId = ++vehicleSelectionRequests[model]
      try {
        const vehicle = await fetchCarById(value.id)
        if (vehicleSelectionRequests[model] === requestId) {
          wizard.setItem(model, vehicle)
        }
      } catch {
        // The search result is sufficient to continue if the detail request fails.
      }
    }

    function goStep(entity) {
      router.push(`/executors/new/${entity}`)
    }

    async function saveSplitForm(model, payload) {
      const data = payload && payload.model ? payload : { model, form: payload }
      const response = await store.dispatch(data.form?.id ? 'apiStorage/updateItem' : 'apiStorage/addItem', data)
      if (!response) return false
      if (response.car || response.trailer) {
        if (response.car) wizard.setItem('cars', response.car)
        if (response.trailer) wizard.setItem('trailers', response.trailer)
      } else {
        wizard.setItem(model, response)
      }
      return true
    }

    const detailRoutes = {
      companies: 'CompanyDetail',
      managers: 'ManagerDetail',
      drivers: 'DriverDetail',
      cars: 'CarDetail',
      trailers: 'TrailerDetail',
    }

    function editItem(model) {
      const item = wizard.selectItems[model]
      const name = detailRoutes[model]
      if (!item?.id || !name) return
      router.push({
        name,
        params: { id: String(item.id) },
        query: { returnTo: 'executor-wizard', wizardModel: model },
      })
    }

    function openCard(model, entity) {
      if (wizard.selectItems[model]) {
        editItem(model)
      } else {
        goStep(entity)
      }
    }

    function goToList() {
      router.push({ name: 'Executors' })
    }

    async function createExecutor() {
      const s = wizard.selectItems
      const form = {
        company_id: s.companies.id,
        car_id: s.cars.id,
        driver_id: s.drivers.id,
        manager_id: s.managers.id,
        trailer_id: s.trailers ? s.trailers.id : null,
      }

      isSaving.value = true
      createError.value = null
      try {
        const response = await store.dispatch('apiStorage/addItem', {
          model: 'executors',
          form,
        })
        if (!response) {
          isSaving.value = false
          return
        }
        const newId = response.id || response.executor_id
        wizard.setLastCreated(newId)
        wizard.reset()
        if (newId) {
          router.push({ name: 'ExecutorsDetail', params: { id: String(newId) } })
        } else {
          router.push({ name: 'Executors' })
        }
      } catch (err) {
        createError.value = err?.message || 'Не удалось создать исполнителя'
      } finally {
        isSaving.value = false
      }
    }

    function confirmClear() {
      if (window.confirm('Очистить все выбранные записи формы?')) {
        wizard.reset()
      }
    }

    return {
      wizard,
      split,
      isSaving,
      createError,
      progressDone,
      progressPercent,
      hasAnySelection,
      onCompanySelect,
      onItemSelect,
      goStep,
      editItem,
      openCard,
      goToList,
      createExecutor,
      saveSplitForm,
      confirmClear,
    }
  },
}
</script>

<style scoped>
.btn {
  height: 32px;
  border: 1px solid #d7dce2;
  border-radius: 4px;
  background: #fff;
  color: #333;
  cursor: pointer;
  font-size: 13px;
  padding: 0 12px;
}

.split-toggle { color: #374151; font-size: 12px; }
.split-toggle input { margin-right: 6px; }

.btn:hover {
  border-color: #007bff;
  color: #007bff;
}

.back-list-button {
  white-space: nowrap;
}

.hub-progress {
  align-items: center;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  padding: 14px 18px;
}

.hub-progress .select-button,
.hub-progress .btn-clear {
  margin-top: 0;
  width: auto;
}

.hub-progress .select-button.primary {
  padding: 10px 16px;
  width: auto;
}

.progress-error {
  color: #b42318;
  font-size: 12px;
}

.progress-label {
  color: #495057;
  font-size: 13px;
  font-weight: 600;
}

.progress-track {
  flex: 1;
  height: 8px;
  background-color: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background-color: #28a745;
  transition: width 0.25s ease-in-out;
}

.progress-count {
  color: #495057;
  font-size: 13px;
  min-width: 36px;
  text-align: right;
}

.hub-card-title {
  align-items: center;
  color: #111827;
  display: flex;
  font-size: 18px;
  font-weight: 700;
  gap: 6px;
  margin: 0 0 12px;
}

.ok-icon {
  color: #28a745;
  font-size: 14px;
}

.field-actions {
  display: flex;
  justify-content: center;
  margin-top: 12px;
}

.select-button {
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  background-color: #2563eb;
  color: #fff;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
}

.select-button:hover {
  background-color: #1d4ed8;
}

.select-button.primary {
  background-color: #28a745;
  padding: 12px 20px;
  font-size: 14px;
  width: 100%;
}

.select-button.primary:hover {
  background-color: #218838;
}

.link-button {
  margin-top: 12px;
  background: transparent;
  border: none;
  color: #6c757d;
  cursor: pointer;
  font-size: 12px;
  text-decoration: underline;
  width: 100%;
}

.link-button:hover {
  color: #495057;
}

.btn-outline {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: #fff;
  border: 1px solid #d7dce2;
  border-radius: 8px;
  color: #374151;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  padding: 10px 16px;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.btn-outline:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #6c757d;
  color: #111827;
}

.btn-clear {
  margin-top: 10px;
  width: 100%;
}

.validation-error {
  color: #dc3545;
  font-size: 13px;
  margin-bottom: 8px;
  text-align: center;
}

.saving {
  color: #6c757d;
  font-size: 13px;
  padding: 10px 0;
  text-align: center;
}

.incomplete-hint {
  color: #6c757d;
  font-size: 13px;
  padding: 8px 10px;
  margin-bottom: 8px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  line-height: 1.4;
}

@media (max-width: 1100px) {
  :deep(.form-grid) {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  }
}

@media (max-width: 720px) {
  :deep(.form-grid) {
    grid-template-columns: 1fr !important;
  }
}
</style>
